import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import LegalService from "../services/LegalService";
import { LegalDocument } from "../types/legal";

interface PublicLegalDocumentProps {
  type: "privacy" | "tos" | "eula" | "cookie" | "dpa";
}

const PublicLegalDocument: React.FC<PublicLegalDocumentProps> = ({ type }) => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [document, setDocument] = useState<LegalDocument | null>(null);

  const appParam = useMemo(() => searchParams.get("app")?.trim() || "", [searchParams]);
  const title = (() => {
    switch (type) {
      case "privacy":
        return "Privacy Policy";
      case "tos":
        return "Terms of Service";
      case "eula":
        return "End User License Agreement";
      case "cookie":
        return "Cookie Policy";
      case "dpa":
        return "Data Processing Agreement";
      default:
        return "Legal Document";
    }
  })();

  useEffect(() => {
    const loadPublicDocument = async () => {
      if (!appParam) {
        setLoading(false);
        setError('Missing required query parameter: "app".');
        return;
      }

      try {
        setLoading(true);
        setError("");
        const result = await LegalService.getPublicDocumentByAppId(appParam, type);
        if (!result) {
          setError(`No ${title.toLowerCase()} is currently available.`);
          setDocument(null);
          return;
        }
        setDocument(result);
      } catch (err) {
        console.error("Failed to load public legal document:", err);
        setError(`Failed to load ${title.toLowerCase()}.`);
      } finally {
        setLoading(false);
      }
    };

    loadPublicDocument();
  }, [appParam, title, type]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #f7f9fc 0%, #f3f5f8 55%, #f0f3f7 100%)",
        py: { xs: 4, md: 7 },
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "#ffffff",
          }}
        >
          <Stack spacing={0.75} sx={{ mb: 4 }}>
            <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 1 }}>
              Legal Document
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {document && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Last updated: {document.lastUpdated.toLocaleDateString()}
              </Typography>
            )}
          </Stack>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="warning">{error}</Alert>
          ) : document ? (
            <Box
              sx={{
                color: "text.primary",
                "& h1": { display: "none" },
                "& h2, & h3": { mt: 3.25, mb: 1.5, fontWeight: 700 },
                "& p": { lineHeight: 1.75, mb: 1.5, fontSize: "0.98rem" },
                "& ul, & ol": { pl: 3, mb: 2 },
                "& li": { mb: 0.7, lineHeight: 1.7 },
                "& hr": { my: 3, borderColor: "divider" },
              }}
              dangerouslySetInnerHTML={{ __html: document.htmlContent }}
            />
          ) : null}
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicLegalDocument;
